import React, { Component, useState } from "react";
import { Button, Spin, Tag, notification } from "antd";
import {
  CloseOutlined, ReloadOutlined, BellOutlined,
  CheckOutlined, RightOutlined, ThunderboltOutlined,
} from "@ant-design/icons";
import {
  detectSignals, getCachedSignals, setCachedSignals,
  getSignalCount, getPrioritySignals, SIGNAL_CONFIG, PRIORITY,
} from "./SignalDetector";
import { list } from "../api/Apis";
import { withAppContext } from "../store/contextStore";

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const GREEN  = "#2D6A4F";
const WHITE  = "#FFFFFF";
const BG     = "#F0F3FF";
const BORDER = "#E2E8F0";
const MUTED  = "#718096";
const DARK   = "#0D1140";

// ── Buddha Assistant avatar — uses real logo image ────────────────────────────
// Upload the logo to S3 and update BUDDHA_LOGO_URL to the public URL.
// Falls back to initials if image fails to load.
const BUDDHA_LOGO_URL = process.env.REACT_APP_BUDDHA_ASSISTANT_LOGO ||
  "https://homeslistedby-prod-attachments.s3.amazonaws.com/public/assistant-buddha-logo.png";

const BuddhaAvatar = ({ size = 48, pulse = false }) => {
  const [imgError, setImgError] = React.useState(false);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 60%, ${GREEN} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: `0 4px 16px rgba(26,34,128,0.35)`,
      flexShrink: 0, position: "relative", overflow: "hidden",
      animation: pulse ? "buddhaBreath 2.5s ease-in-out infinite" : "none",
    }}>
      {!imgError ? (
        <img
          src={BUDDHA_LOGO_URL}
          alt="Home Buddha Assistant"
          onError={() => setImgError(true)}
          style={{
            width: "88%", height: "88%", objectFit: "contain",
            // Black background on the PNG — use mix-blend-mode to drop it
            mixBlendMode: "lighten",
            filter: "brightness(1.1)",
          }}
        />
      ) : (
        // Fallback if image not yet uploaded
        <span style={{ fontSize: size * 0.38, color: GOLD, fontWeight: 800 }}>🏠</span>
      )}
      <style>{`@keyframes buddhaBreath { 0%,100%{transform:scale(1);} 50%{transform:scale(1.04);} }`}</style>
    </div>
  );
};

// ── Priority badge config ─────────────────────────────────────────────────────
const PRIORITY_BADGE = {
  urgent: { color: "#E53E3E", bg: "#FFF5F5", label: "Urgent" },
  high:   { color: "#D97706", bg: "#FFFBF0", label: "Action Needed" },
  medium: { color: NAVY,      bg: "#EEF2FF", label: "Heads Up"  },
  low:    { color: MUTED,     bg: "#F7F9FC", label: "FYI"       },
  info:   { color: MUTED,     bg: "#F7F9FC", label: ""          },
};

// ── Signal Card ───────────────────────────────────────────────────────────────
function SignalCard({ signal, onDismiss, onAction }) {
  const cfg    = SIGNAL_CONFIG[signal.type] || { icon: "💡", color: NAVY };
  const badge  = PRIORITY_BADGE[signal.priority] || PRIORITY_BADGE.info;
  const isInfo = signal.priority === "info";

  return (
    <div style={{
      background: signal.priority === "urgent" ? "#FFF5F5" : WHITE,
      borderRadius: "14px",
      border: `1.5px solid ${signal.priority === "urgent" ? "#FED7D7" : BORDER}`,
      padding: "14px 16px",
      marginBottom: "10px",
      transition: "all .2s",
      borderLeft: `4px solid ${cfg.color}`,
    }}>
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        {/* Icon */}
        <div style={{
          width: "38px", height: "38px", borderRadius: "10px",
          background: `${cfg.color}15`, display: "flex",
          alignItems: "center", justifyContent: "center",
          fontSize: "18px", flexShrink: 0,
        }}>
          {cfg.icon}
        </div>
        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", fontWeight: 700, color: NAVY, lineHeight: 1.3 }}>{signal.title}</span>
            {badge.label && (
              <span style={{
                fontSize: "10px", fontWeight: 700, color: badge.color,
                background: badge.bg, padding: "2px 7px", borderRadius: "20px",
                border: `1px solid ${badge.color}30`,
              }}>{badge.label}</span>
            )}
          </div>
          <div style={{ fontSize: "12px", color: MUTED, lineHeight: 1.55, marginBottom: signal.cta ? "10px" : 0 }}>
            {signal.body}
          </div>
          {signal.cta && (
            <button onClick={() => onAction(signal)}
              style={{
                background: cfg.color, color: WHITE, border: "none",
                borderRadius: "50px", padding: "6px 14px",
                fontSize: "11px", fontWeight: 700, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: "4px",
              }}>
              {signal.cta.label} <RightOutlined style={{ fontSize: "9px" }} />
            </button>
          )}
        </div>
        {/* Dismiss */}
        {signal.dismissible && (
          <button onClick={() => onDismiss(signal.id)}
            style={{ background: "none", border: "none", cursor: "pointer", color: MUTED, fontSize: "13px", padding: "2px", flexShrink: 0 }}>
            <CloseOutlined />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
class AssistantPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible:       false,  // controlled externally via props OR internally
      signals:       [],
      loading:       true,
      aiSummary:     "",
      aiLoading:     false,
      dismissed:     [],     // IDs dismissed this session
      activeFilter:  "all",  // all / urgent / action / info
      hasShownToday: false,
    };
  }

  componentDidMount() {
    // Auto-show on first daily login if not suppressed
    const { autoShow = true } = this.props;
    if (autoShow) this.checkAutoShow();
    this.loadSignals();
  }

  componentDidUpdate(prevProps) {
    // If parent opens panel
    if (this.props.visible && !prevProps.visible) {
      this.setState({ visible: true });
      if (this.state.signals.length === 0) this.loadSignals();
    }
  }

  checkAutoShow = () => {
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem("mhb_assistant_shown");
    if (lastShown !== today) {
      // Show after short delay so portal loads first
      setTimeout(() => {
        this.setState({ visible: true });
        localStorage.setItem("mhb_assistant_shown", today);
      }, 1200);
    }
  };

  loadSignals = async () => {
    const { userDetails } = this.props.context.state;
    if (!userDetails?.id) { this.setState({ loading: false }); return; }
    this.setState({ loading: true });

    // Check session cache first
    const cached = getCachedSignals(userDetails.id);
    if (cached) { this.setState({ signals: cached, loading: false }); return; }

    try {
      // Load mybuddha records
      const resp = await list("mybuddha", { userId: userDetails.id });
      const records = resp.success ? (resp.data || []) : [];
      const signals = detectSignals(userDetails, records);
      setCachedSignals(userDetails.id, signals);
      this.setState({ signals, loading: false });
      // Generate AI summary if we have signals
      if (signals.filter(s => s.priority !== "info").length > 0) {
        this.generateSummary(userDetails, signals);
      }
    } catch(e) {
      console.error("AssistantPanel load:", e);
      this.setState({ loading: false });
    }
  };

  generateSummary = async (userDetails, signals) => {
    this.setState({ aiLoading: true });
    const firstName = userDetails?.firstName || "there";
    const profile   = userDetails?.userProfile || "B";
    const topSignals = signals
      .filter(s => ["urgent","high","medium"].includes(s.priority))
      .slice(0, 5)
      .map(s => `• [${s.priority.toUpperCase()}] ${s.title}: ${s.body}`)
      .join("\n");

    if (!topSignals) { this.setState({ aiLoading: false }); return; }

    const profileLabel = { AG: "real estate agent", L: "lender", B: "home buyer", S: "seller", HO: "homeowner", V: "service provider" }[profile] || "user";

    try {
      const resp = await fetch("https://api.anthropic.com/v1/messages", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model:      "claude-sonnet-4-20250514",
          max_tokens: 200,
          messages: [{
            role:    "user",
            content: `You are the My Home Buddha AI Assistant — warm, knowledgeable, and concise. Write a personalized 2-3 sentence morning briefing for ${firstName}, a ${profileLabel}. Summarize the most important things they should know today based on these signals:\n\n${topSignals}\n\nBe warm and personal, not robotic. Don't list everything — just highlight the most important 1-2 things conversationally. End with an encouraging note. Keep it under 60 words.`,
          }],
        }),
      });
      const data = await resp.json();
      const text = data?.content?.[0]?.text || "";
      this.setState({ aiSummary: text, aiLoading: false });
    } catch(e) {
      this.setState({ aiLoading: false });
    }
  };

  handleDismiss = (id) => {
    this.setState(p => ({ dismissed: [...p.dismissed, id] }));
  };

  handleAction = (signal) => {
    const { cta } = signal;
    if (!cta) return;
    if (cta.url) {
      window.open(cta.url, cta.url.startsWith("http") ? "_blank" : "_self");
    }
    if (cta.action && this.props.onAction) {
      this.props.onAction(cta.action, signal);
    }
    this.handleDismiss(signal.id);
  };

  handleClose = () => {
    this.setState({ visible: false });
    if (this.props.onClose) this.props.onClose();
  };

  handleRefresh = () => {
    const { userDetails } = this.props.context.state;
    if (userDetails?.id) {
      const { clearSignalCache } = require("./SignalDetector");
      clearSignalCache(userDetails.id);
    }
    this.setState({ signals: [], aiSummary: "", dismissed: [] });
    this.loadSignals();
  };

  render() {
    const { visible, signals, loading, aiSummary, aiLoading, dismissed, activeFilter } = this.state;
    const { userDetails } = this.props.context.state;
    const firstName = userDetails?.firstName || "there";
    const profile   = userDetails?.userProfile || "B";

    if (!visible) return null;

    // Filter out dismissed
    const activeSignals = signals.filter(s => !dismissed.includes(s.id));
    const urgentCount   = activeSignals.filter(s => ["urgent","high"].includes(s.priority)).length;

    // Apply filter
    const filteredSignals = activeSignals.filter(s => {
      if (activeFilter === "all")    return true;
      if (activeFilter === "urgent") return ["urgent","high"].includes(s.priority);
      if (activeFilter === "action") return s.cta !== null;
      if (activeFilter === "info")   return s.priority === "info";
      return true;
    });

    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    const profileLabel = { AG: "Agent", L: "Lender", B: "Buyer", S: "Seller", HO: "Homeowner", V: "Vendor" }[profile] || "";

    return (
      <>
        {/* Backdrop */}
        <div onClick={this.handleClose}
          style={{ position: "fixed", inset: 0, background: "rgba(10,15,50,0.5)", backdropFilter: "blur(3px)", zIndex: 9998 }} />

        {/* Panel */}
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(440px, 100vw)",
          background: WHITE,
          zIndex: 9999,
          display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(26,34,128,0.18)",
          animation: "slideInRight .3s cubic-bezier(.4,0,.2,1)",
        }}>
          <style>{`
            @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            .signal-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
            .filter-pill:hover { background: #E8EBF8 !important; }
          `}</style>

          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div style={{
            background: `linear-gradient(135deg, ${DARK} 0%, ${NAVY} 55%, #1A3A60 100%)`,
            padding: "20px 20px 16px",
            borderBottom: `3px solid ${GOLD}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <BuddhaAvatar size={46} pulse={urgentCount > 0} />
                <div>
                  <div style={{ color: GOLD, fontWeight: 800, fontSize: "15px", letterSpacing: "-0.01em" }}>
                    Home Buddha Assistant
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", marginTop: "1px" }}>
                    {profileLabel} Dashboard
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={this.handleRefresh}
                  style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: "32px", height: "32px", cursor: "pointer", color: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ReloadOutlined style={{ fontSize: "13px" }} />
                </button>
                <button onClick={this.handleClose}
                  style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "50%", width: "32px", height: "32px", cursor: "pointer", color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>
                  <CloseOutlined />
                </button>
              </div>
            </div>

            {/* Greeting */}
            <div style={{ color: WHITE, fontSize: "16px", fontWeight: 800, marginBottom: "4px" }}>
              {greeting}, {firstName}! {urgentCount > 0 ? "⚡" : "👋"}
            </div>

            {/* AI Summary */}
            {aiLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "rgba(255,255,255,0.55)", fontSize: "12px" }}>
                <Spin size="small" style={{ color: GOLD }} /> Preparing your briefing...
              </div>
            )}
            {aiSummary && !aiLoading && (
              <div style={{
                background: "rgba(255,255,255,0.08)", borderRadius: "10px",
                padding: "10px 14px", fontSize: "13px", color: "rgba(255,255,255,0.85)",
                lineHeight: 1.6, borderLeft: `3px solid ${GOLD}`,
                animation: "fadeIn .5s ease",
              }}>
                {aiSummary}
              </div>
            )}
            {!aiSummary && !aiLoading && activeSignals.length === 0 && (
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "13px" }}>
                You're all caught up — no new alerts today! ✅
              </div>
            )}
            {!aiSummary && !aiLoading && activeSignals.length > 0 && (
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px" }}>
                You have <strong style={{ color: GOLD }}>{activeSignals.length}</strong> item{activeSignals.length !== 1 ? "s" : ""} on your radar
                {urgentCount > 0 && <>, including <strong style={{ color: "#FC8181" }}>{urgentCount} urgent</strong></>}.
              </div>
            )}

            {/* Summary stats row */}
            {activeSignals.length > 0 && (
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                {[
                  { label: "Urgent",  count: activeSignals.filter(s => s.priority === "urgent").length,  color: "#FC8181" },
                  { label: "Action",  count: activeSignals.filter(s => s.priority === "high").length,    color: GOLD },
                  { label: "Info",    count: activeSignals.filter(s => ["medium","low","info"].includes(s.priority)).length, color: "rgba(255,255,255,0.5)" },
                ].filter(i => i.count > 0).map(({ label, count, color }) => (
                  <div key={label} style={{ background: "rgba(255,255,255,0.08)", borderRadius: "8px", padding: "4px 10px", textAlign: "center" }}>
                    <div style={{ fontSize: "15px", fontWeight: 800, color }}>{count}</div>
                    <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.45)" }}>{label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Filter pills ─────────────────────────────────────────────────── */}
          {activeSignals.length > 0 && (
            <div style={{ padding: "12px 20px 8px", background: BG, borderBottom: `1px solid ${BORDER}`, display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {[
                { key: "all",    label: `All (${activeSignals.length})` },
                { key: "urgent", label: `⚡ Urgent/High (${activeSignals.filter(s=>["urgent","high"].includes(s.priority)).length})` },
                { key: "action", label: `▶ Has Action (${activeSignals.filter(s=>s.cta).length})` },
                { key: "info",   label: `💡 Info (${activeSignals.filter(s=>s.priority==="info").length})` },
              ].filter(f => {
                if (f.key === "urgent") return activeSignals.filter(s=>["urgent","high"].includes(s.priority)).length > 0;
                if (f.key === "action") return activeSignals.filter(s=>s.cta).length > 0;
                if (f.key === "info")   return activeSignals.filter(s=>s.priority==="info").length > 0;
                return true;
              }).map(({ key, label }) => (
                <button key={key} className="filter-pill"
                  onClick={() => this.setState({ activeFilter: key })}
                  style={{
                    background: activeFilter === key ? NAVY : WHITE,
                    color:      activeFilter === key ? WHITE : MUTED,
                    border:     `1.5px solid ${activeFilter === key ? NAVY : BORDER}`,
                    borderRadius: "50px", padding: "4px 12px",
                    fontSize: "11px", fontWeight: activeFilter === key ? 700 : 500,
                    cursor: "pointer", transition: "all .15s",
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* ── Signal list ──────────────────────────────────────────────────── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
            {loading && (
              <div style={{ textAlign: "center", padding: "50px", color: MUTED }}>
                <BuddhaAvatar size={52} pulse />
                <div style={{ marginTop: "14px", fontSize: "14px", fontWeight: 600 }}>Reading your home data...</div>
                <div style={{ fontSize: "12px", color: MUTED, marginTop: "4px" }}>Generating your personalized briefing</div>
              </div>
            )}

            {!loading && filteredSignals.length === 0 && (
              <div style={{ textAlign: "center", padding: "50px 20px", color: MUTED }}>
                <div style={{ fontSize: "40px", marginBottom: "12px" }}>✅</div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: NAVY, marginBottom: "6px" }}>
                  {activeFilter === "all" ? "All clear!" : "Nothing here"}
                </div>
                <div style={{ fontSize: "13px" }}>
                  {activeFilter === "all"
                    ? "No alerts right now. Your Home Buddha is keeping watch."
                    : "No items match this filter."}
                </div>
              </div>
            )}

            {!loading && filteredSignals.map((signal, i) => (
              <div key={signal.id} className="signal-card" style={{ animation: `fadeIn .3s ease ${i * 0.05}s both` }}>
                <SignalCard
                  signal={signal}
                  onDismiss={this.handleDismiss}
                  onAction={this.handleAction}
                />
              </div>
            ))}

            {/* Dismissed note */}
            {dismissed.length > 0 && !loading && (
              <div style={{ textAlign: "center", marginTop: "8px" }}>
                <button onClick={() => this.setState({ dismissed: [] })}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: "12px", color: MUTED, textDecoration: "underline" }}>
                  Restore {dismissed.length} dismissed item{dismissed.length !== 1 ? "s" : ""}
                </button>
              </div>
            )}
          </div>

          {/* ── Footer ───────────────────────────────────────────────────────── */}
          <div style={{
            padding: "14px 20px", borderTop: `1px solid ${BORDER}`,
            background: BG, display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <BuddhaAvatar size={26} />
              <span style={{ fontSize: "11px", color: MUTED }}>Home Buddha Assistant · Updated {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <button onClick={this.handleClose}
              style={{
                background: NAVY, color: WHITE, border: "none",
                borderRadius: "50px", padding: "7px 16px",
                fontSize: "12px", fontWeight: 700, cursor: "pointer",
              }}>
              Done ✓
            </button>
          </div>
        </div>
      </>
    );
  }
}

export default withAppContext(AssistantPanel);

// ── Assistant Trigger Button (for nav bar) ─────────────────────────────────────
// Drop this wherever you want the nav trigger — header, dropdown, etc.
export function AssistantTrigger({ signalCount = 0, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: "none", border: `1.5px solid ${BORDER}`,
        borderRadius: "50px", padding: "6px 14px 6px 8px",
        cursor: "pointer", transition: "all .2s",
        color: NAVY, fontWeight: 700, fontSize: "13px",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = BG; e.currentTarget.style.borderColor = NAVY; }}
      onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.borderColor = BORDER; }}>
      <BuddhaAvatar size={28} pulse={signalCount > 0} />
      <span>My Assistant</span>
      {signalCount > 0 && (
        <span style={{
          background: "#E53E3E", color: WHITE,
          borderRadius: "50%", width: "18px", height: "18px",
          fontSize: "10px", fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {signalCount > 9 ? "9+" : signalCount}
        </span>
      )}
    </button>
  );
}